import { z } from "zod";
export type AssistantJson =
  | null
  | boolean
  | number
  | string
  | AssistantJson[]
  | { [key: string]: AssistantJson };
export const assistantJsonSchema: z.ZodType<AssistantJson> = z.lazy(() =>
  z.union([
    z.null(),
    z.boolean(),
    z.number().finite(),
    z.string(),
    z.array(assistantJsonSchema),
    z.record(assistantJsonSchema),
  ]),
);
const id = z.string().min(1).max(500);
export const assistantContextSchema = z
  .object({
    projectId: id,
    branchId: id.nullable(),
    versionId: id.nullable(),
    editorInstanceId: id,
    revisionNum: z.number().int().min(0),
    changeCounter: z.number().int().min(0),
  })
  .strict();
export const assistantCallSchema = z
  .object({
    protocolVersion: z.literal(1),
    runId: id,
    callId: id,
    toolName: id,
    arguments: z.record(assistantJsonSchema),
    expectedContext: assistantContextSchema,
  })
  .strict();
export const assistantStyleOmissionSchema = z.object({
  key: z.string().min(1).max(200).regex(/^(?:--)?[a-z][a-z0-9-]*$/),
  reason: z.enum(["empty_value", "invalid_value", "sanitizer_error", "not_settable",
    "animation_unsupported", "not_applicable_to_element"]),
}).strict();
/** Issued only by the executor after trusted native transaction verification.
 * A digest identifies captured evidence; it is not a signature or a client grant. */
export const assistantPartialProofSchema = z.object({
  kind: z.literal("verified_omissions"),
  runId: id,
  callId: id,
  toolName: z.literal("set_element_styles"),
  afterChangeCounter: z.number().int().nonnegative(),
  omissions: z.array(assistantStyleOmissionSchema).min(1).max(200),
  appliedKeys: z.array(assistantStyleOmissionSchema.shape.key).max(1000),
  digest: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
export const assistantResultSchema = z
  .object({
    protocolVersion: z.literal(1),
    runId: id,
    callId: id,
    toolName: id,
    outcome: z.enum([
      "not_applied",
      "applied",
      "partially_applied",
      "outcome_unknown",
    ]),
    output: assistantJsonSchema,
    warnings: z.array(z.string()).max(200),
    affectedIds: z.array(id).max(10000),
    beforeContext: assistantContextSchema.optional(),
    afterContext: assistantContextSchema.optional(),
    partialProof: assistantPartialProofSchema.optional(),
    error: z
      .object({ type: id, message: z.string().max(2000) })
      .strict()
      .optional(),
  })
  .strict();
export type AssistantContext = z.infer<typeof assistantContextSchema>;
export type AssistantCall = z.infer<typeof assistantCallSchema>;
export type AssistantResult = z.infer<typeof assistantResultSchema>;
/** A committed edit with a failed acknowledgement read needs reconciliation,
 * just as a partial or unknown outcome does. All clients share this boundary.
 */
export function requiresAssistantReconciliation(
  result: AssistantResult,
): boolean {
  if (result.outcome === "partially_applied") {
    const parsed = assistantResultSchema.safeParse(result);
    if (!parsed.success) return true;
    const r = parsed.data;
    const p = r.partialProof;
    const b = r.beforeContext;
    const a = r.afterContext;
    if (!p || !b || !a || r.error || r.toolName !== "set_element_styles" ||
        p.runId !== r.runId || p.callId !== r.callId || p.toolName !== r.toolName ||
        p.afterChangeCounter !== a.changeCounter ||
        b.projectId !== a.projectId || b.branchId !== a.branchId ||
        b.versionId !== a.versionId || b.editorInstanceId !== a.editorInstanceId ||
        a.revisionNum < b.revisionNum || a.changeCounter < b.changeCounter) return true;
    const omitted = new Set(p.omissions.map(o => o.key));
    return omitted.size !== p.omissions.length ||
      new Set(p.appliedKeys).size !== p.appliedKeys.length ||
      p.appliedKeys.some(k => omitted.has(k));
  }
  return (
    result.outcome === "outcome_unknown" ||
    (result.outcome === "applied" && result.error !== undefined)
  );
}
export const assistantPolicySchema = z
  .object({
    schemaVersion: z.literal(1),
    location: z.enum(["host_document", "browser_view", "server_resource"]),
    effect: z.enum([
      "source_read",
      "document_edit",
      "history",
      "save",
      "external",
    ]),
    permission: z.enum(["read", "edit"]),
    dependency: z.enum(["available", "blocked"]),
    /** Typed recovery marker: only the exact held hosted effect may be read
     * while an acknowledgement is uncertain. Never a blanket effect bypass. */
    recovery: z.literal("owned_hosted_branch_effect").optional(),
  })
  .strict();
export type AssistantPolicy = z.infer<typeof assistantPolicySchema>;
/** Canonical argument/context fingerprint, retained privately in the result ledger. */
export function fingerprintAssistantCall(call: AssistantCall): string {
  function sorted(value: AssistantJson): AssistantJson {
    if (Array.isArray(value)) return value.map(sorted);
    if (value !== null && typeof value === "object")
      return Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((k) => [k, sorted(value[k])]),
      );
    return value;
  }
  return JSON.stringify(
    sorted({
      toolName: call.toolName,
      arguments: call.arguments,
      expectedContext: call.expectedContext,
    }),
  );
}

/** Native acknowledged persistence; source edits and publication are separate states. */
export const assistantSaveReceiptSchema = z
  .object({
    projectId: id,
    branchId: id.nullable(),
    versionId: id.nullable(),
    editorInstanceId: id,
    revisionNum: z.number().int().min(0),
    savedChangeCounter: z.number().int().min(0),
    hasUnsavedChanges: z.boolean(),
  })
  .strict();

const historyCount = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
/** Safe native values only; never expose records or model/view references. */
export const assistantHistorySnapshotSchema = z
  .object({
    instanceId: id,
    version: historyCount,
    cursor: historyCount,
    length: historyCount,
    canUndo: z.boolean(),
    canRedo: z.boolean(),
  })
  .strict()
  .refine(
    (value) =>
      value.cursor <= value.length &&
      value.canUndo === value.cursor > 1 &&
      value.canRedo === value.cursor < value.length,
    "Native history availability must match its cursor and length",
  );
export const assistantHistoryInputSchema = z
  .object({ expectedHistory: assistantHistorySnapshotSchema })
  .strict();
export const assistantHistoryReceiptSchema = z
  .object({
    direction: z.enum(["undo", "redo"]),
    beforeHistory: assistantHistorySnapshotSchema,
    afterHistory: assistantHistorySnapshotSchema,
    beforeChangeCounter: historyCount,
    afterChangeCounter: historyCount,
  })
  .strict()
  .refine(
    ({
      direction,
      beforeHistory: before,
      afterHistory: after,
      beforeChangeCounter,
      afterChangeCounter,
    }) =>
      before.instanceId === after.instanceId &&
      after.version === before.version + 1 &&
      before.length === after.length &&
      (direction === "undo" ? before.canUndo : before.canRedo) &&
      after.cursor === before.cursor + (direction === "undo" ? -1 : 1) &&
      afterChangeCounter >= beforeChangeCounter,
    "Native history receipt must acknowledge one step in the same log",
  );
